import { useCallback, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { BusinessLineSegment, BusinessLineDailyRow } from '@monitoring-panel/types';
import { formatNumber, formatDate } from '@/lib/utils';
import {
  ADVIA_CHART_OPTS,
  ADVIA_ECHARTS_THEME,
  REVENUE_COLORS,
  darkStop,
  solid,
  tooltipRow,
  tooltipTitle,
} from '@/lib/advia-charts';

interface BusinessLinesTableProps {
  segments: BusinessLineSegment[];
}

/**
 * One business line as the table shows it: every segment that survived the
 * filters, summed by product and channel.
 */
interface BusinessLine {
  key: string;
  product: string;
  channel: string;
  label: string;
  revenue: number;
  forecastedRevenue: number;
  totalRevenue: number;
  mediaCost: number;
  margin: number;
  marginPercentage: number;
  units: number;
  /** Effective price sold and paid per unit, null when they can't be compared. */
  soldPrice: number | null;
  costPrice: number | null;
  priceUnit: string;
  daysWithRevenue: number;
  daily: BusinessLineDailyRow[];
}

// Same palette as the main daily revenue chart, so each row reads as its detail.
const COLORS = {
  actualRevenue: solid(REVENUE_COLORS.served),
  closingRevenue: solid(REVENUE_COLORS.closing),
  forecastedRevenue: solid(REVENUE_COLORS.forecast),
  margin: solid(REVENUE_COLORS.margin),
} as const;

/** Margin below `warning` is flagged red, at or above `healthy` green. */
const MARGIN_THRESHOLDS = {
  healthy: 30,
  warning: 15,
} as const;

/**
 * Below this many days with actual revenue, a line's margin rests on so few
 * days that it swings wildly — worth flagging rather than reading as a trend.
 */
const THIN_DATA_DAYS = 5;

// Tall enough for a real pair of axes and for the tooltip to fit inside the
// row: the table wrapper scrolls horizontally, and a scroll container clips on
// both axes, so a tooltip taller than its row would be cut off.
const ROW_CHART_HEIGHT = 150;

export function marginTextClass(marginPercentage: number): string {
  if (marginPercentage >= MARGIN_THRESHOLDS.healthy) return 'text-adv-success';
  if (marginPercentage >= MARGIN_THRESHOLDS.warning) return 'text-adv-accent';
  return 'text-adv-warning';
}

function marginBarClass(marginPercentage: number): string {
  if (marginPercentage >= MARGIN_THRESHOLDS.healthy) return 'bg-adv-success';
  if (marginPercentage >= MARGIN_THRESHOLDS.warning) return 'bg-adv-gold';
  return 'bg-adv-warning';
}

/** CPM and vCPM are quoted per thousand units; CPC and CPV per single unit. */
function priceMultiplier(priceType: string): number {
  return priceType.toUpperCase().includes('CPM') ? 1000 : 1;
}

/**
 * Sum the segments that pass the filters into one row per product and channel.
 *
 * Effective prices are only emitted when every segment on the line shares a
 * price type: a CPM of 3 € and a CPC of 0.30 € are different units, and their
 * weighted average is a number that means nothing.
 */
function aggregateLines(segments: BusinessLineSegment[]): BusinessLine[] {
  const grouped = new Map<string, BusinessLineSegment[]>();
  for (const segment of segments) {
    const key = `${segment.product}::${segment.channel}`;
    const existing = grouped.get(key);
    if (existing) existing.push(segment);
    else grouped.set(key, [segment]);
  }

  const lines: BusinessLine[] = [];

  for (const [key, group] of grouped) {
    const dates = group[0].daily.map((day) => day.date);
    const daily: BusinessLineDailyRow[] = dates.map((date, index) => ({
      date,
      revenue: group.reduce((sum, s) => sum + (s.daily[index]?.revenue ?? 0), 0),
      closedRevenue: group.reduce((sum, s) => sum + (s.daily[index]?.closedRevenue ?? 0), 0),
      forecastedRevenue: group.reduce(
        (sum, s) => sum + (s.daily[index]?.forecastedRevenue ?? 0),
        0
      ),
      mediaCost: group.reduce((sum, s) => sum + (s.daily[index]?.mediaCost ?? 0), 0),
      units: group.reduce((sum, s) => sum + (s.daily[index]?.units ?? 0), 0),
    }));

    const revenue = daily.reduce((sum, day) => sum + day.revenue, 0);
    const forecastedRevenue = daily.reduce((sum, day) => sum + day.forecastedRevenue, 0);
    const mediaCost = daily.reduce((sum, day) => sum + day.mediaCost, 0);
    const units = daily.reduce((sum, day) => sum + day.units, 0);
    const margin = revenue - mediaCost;

    const priceTypes = new Set(group.map((s) => s.priceType));
    const comparable = priceTypes.size === 1 && units > 0;
    const multiplier = priceMultiplier(group[0].priceType);

    lines.push({
      key,
      product: group[0].product,
      channel: group[0].channel,
      label: `${group[0].product} - ${group[0].channel}`,
      revenue,
      forecastedRevenue,
      totalRevenue: revenue + forecastedRevenue,
      mediaCost,
      margin,
      marginPercentage: revenue > 0 ? (margin / revenue) * 100 : 0,
      units,
      soldPrice: comparable ? (revenue / units) * multiplier : null,
      costPrice: comparable ? (mediaCost / units) * multiplier : null,
      priceUnit: priceTypes.size === 1 ? group[0].priceType : 'mixed',
      daysWithRevenue: daily.filter((day) => day.revenue > 0).length,
      daily,
    });
  }

  return lines
    .filter((line) => line.totalRevenue > 0)
    .sort((a, b) => b.totalRevenue - a.totalRevenue);
}

function buildRowChartOption(line: BusinessLine): EChartsOption {
  const dates = line.daily.map((day) => formatDate(day.date));
  // Same split as the main chart: the official revenue is served plus closing.
  const servedRevenue = line.daily.map((day) => day.revenue - day.closedRevenue);
  const closedRevenue = line.daily.map((day) => day.closedRevenue);
  const forecastedRevenue = line.daily.map((day) => day.forecastedRevenue);
  // Media cost only accompanies actual revenue, so days with no actual revenue
  // have no margin to show rather than a misleading 0%.
  const marginPercentage = line.daily.map((day) =>
    day.revenue > 0 ? ((day.revenue - day.mediaCost) / day.revenue) * 100 : null
  );

  return {
    animation: false,
    grid: { top: 12, right: 8, bottom: 6, left: 8, containLabel: true },
    tooltip: {
      trigger: 'axis',
      // `cross` puts the value straight onto both axes, so a bar can be read
      // without guessing its magnitude from its height.
      axisPointer: { type: 'cross' },
      // The documented fix for a chart whose ancestors clip: the table wrapper
      // is `overflow-x-auto`, and a scroll container clips on both axes, so a
      // tooltip rendered inside the row is cut off. Rendered on <body> it sizes
      // freely.
      //
      // ECharts writes `z-index:9999999` into the tooltip's inline style and
      // concatenates `extraCssText` after it, where a later declaration wins.
      // So `extraCssText` must stay cosmetic: setting a z-index there drops the
      // tooltip behind the page and it never appears.
      appendTo: 'body',
      extraCssText: 'border-radius:8px;box-shadow:0 4px 16px rgba(41,41,41,.10);',
      formatter: (params: unknown) => {
        // The margin series carries nulls for days with no revenue, and ECharts
        // hands those to the formatter as `undefined` rather than `null`. A
        // formatter that throws produces no tooltip at all — the axis pointer
        // still draws, which makes it look like a styling problem.
        const items = params as Array<{
          seriesName: string;
          value: number | null | undefined;
          marker: string;
          axisValue: string;
        }>;
        if (items.length === 0) return '';

        let result = tooltipTitle(line.label);
        result += `<div style="color:var(--text-secondary);font-family:var(--font-mono);margin-bottom:8px">${items[0].axisValue}</div>`;

        let hasValue = false;
        for (const item of items) {
          if (typeof item.value !== 'number' || !Number.isFinite(item.value)) continue;
          const isMargin = item.seriesName === 'Margin %';
          if (!isMargin && item.value === 0) continue;
          hasValue = true;
          const formatted = isMargin
            ? formatNumber(item.value, 'percentage', 1)
            : formatNumber(item.value, 'euro');
          result += tooltipRow(item.seriesName, formatted, item.marker);
        }

        if (!hasValue) {
          result += `<div style="color:var(--text-tertiary)">No activity</div>`;
        }
        return result;
      },
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: { hideOverlap: true },
    },
    yAxis: [
      {
        type: 'value',
        splitNumber: 2,
        axisLabel: {
          formatter: (value: number) => formatNumber(value, 'euro', 0),
        },
      },
      {
        type: 'value',
        min: 0,
        max: 100,
        splitNumber: 2,
        axisLabel: {
          formatter: (value: number) => formatNumber(value, 'percentage', 0),
        },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: 'Served Revenue',
        type: 'bar',
        stack: 'revenue',
        yAxisIndex: 0,
        barCategoryGap: '20%',
        itemStyle: { color: COLORS.actualRevenue },
        data: servedRevenue,
      },
      {
        name: 'Closing Revenue',
        type: 'bar',
        stack: 'revenue',
        yAxisIndex: 0,
        itemStyle: { color: COLORS.closingRevenue },
        data: closedRevenue,
      },
      {
        name: 'Forecasted Revenue',
        type: 'bar',
        stack: 'revenue',
        yAxisIndex: 0,
        itemStyle: { color: COLORS.forecastedRevenue },
        data: forecastedRevenue,
      },
      {
        name: 'Margin %',
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        symbol: 'none',
        connectNulls: false,
        lineStyle: { color: COLORS.margin, width: 1.5 },
        itemStyle: { color: COLORS.margin },
        data: marginPercentage,
      },
    ],
  };
}

function FilterChips({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: string[];
  selected: Set<string>;
  onToggle: (value: string) => void;
}) {
  if (options.length === 0) return null;

  return (
    <div className="flex items-start gap-2">
      <span className="adv-kpi__label pt-2 w-14 shrink-0">
        {label}
      </span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => {
          const isActive = selected.has(option);
          return (
            <button
              key={option}
              type="button"
              onClick={() => onToggle(option)}
              aria-pressed={isActive}
              className={`adv-chip ${isActive ? 'adv-chip--on' : ''}`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BusinessLineRow({ line }: { line: BusinessLine }) {
  const chartRef = useRef<ReactECharts>(null);
  const chartOption = useMemo(() => buildRowChartOption(line), [line]);

  /**
   * Clear the tooltip and its axis pointer when the pointer leaves the row.
   *
   * Two upstream gaps make this necessary. `globalout` never fires when the
   * pointer travels straight from one row's canvas onto the next, so with the
   * rows stacked the previous tooltip is never told to go away. And `hideTip`
   * alone leaves the axis pointer drawn (apache/echarts#18595, closed as not
   * planned) — `updateAxisPointer` with `currTrigger: 'leave'` is what actually
   * clears it.
   */
  const hideTooltip = useCallback(() => {
    const instance = chartRef.current?.getEchartsInstance();
    if (!instance) return;
    instance.dispatchAction({ type: 'hideTip' });
    instance.dispatchAction({ type: 'updateAxisPointer', currTrigger: 'leave' });
  }, []);

  const isThin = line.daysWithRevenue > 0 && line.daysWithRevenue < THIN_DATA_DAYS;

  return (
    <tr className="hover:bg-adv-sunken transition-colors">
      <td className="px-4 py-2 align-middle">
        <p className="font-bold whitespace-nowrap">{line.product}</p>
        <p className="text-adv-body-s text-adv-secondary mt-0.5">
          {line.channel}
          {line.channel === 'Unclassified' && (
            <span
              className="ml-1 font-bold text-adv-warning-text"
              title="These executions have no execution-type in advia-os"
            >
              !
            </span>
          )}
        </p>
        {isThin && (
          <p
            className="text-adv-label text-adv-warning-text mt-0.5"
            title="Margin over so few days swings widely — read it as an indication, not a trend"
          >
            only {line.daysWithRevenue}d with revenue
          </p>
        )}
      </td>

      <td className="px-2 py-2 align-middle" onMouseLeave={hideTooltip}>
        <div className="min-w-[360px]">
          <ReactECharts
            ref={chartRef}
            option={chartOption}
            style={{ height: ROW_CHART_HEIGHT }}
            onEvents={{ globalout: hideTooltip }}
            theme={ADVIA_ECHARTS_THEME}
            opts={ADVIA_CHART_OPTS}
          />
        </div>
      </td>

      <td className="px-4 py-2 text-right whitespace-nowrap">
        {line.soldPrice === null ? (
          <span className="text-adv-tertiary" title="Line mixes price types — not comparable">
            mixed
          </span>
        ) : (
          <>
            <p className="adv-num font-bold">
              {formatNumber(line.soldPrice, 'euro')}
            </p>
            <p className="text-adv-label text-adv-tertiary">{line.priceUnit}</p>
          </>
        )}
      </td>
      <td className="px-4 py-2 text-right whitespace-nowrap">
        {line.costPrice === null ? (
          <span className="text-adv-tertiary">—</span>
        ) : (
          <>
            <p className="adv-num text-adv-secondary">
              {formatNumber(line.costPrice, 'euro')}
            </p>
            <p className="text-adv-label text-adv-tertiary">{line.priceUnit}</p>
          </>
        )}
      </td>

      <td className="px-4 py-2 text-right adv-num whitespace-nowrap">
        {formatNumber(line.revenue, 'euro')}
      </td>
      <td className="px-4 py-2 text-right adv-num text-adv-secondary whitespace-nowrap">
        {formatNumber(line.forecastedRevenue, 'euro')}
      </td>
      <td className="px-4 py-2 text-right adv-num text-adv-secondary whitespace-nowrap">
        {formatNumber(line.mediaCost, 'euro')}
      </td>
      <td className="px-4 py-2 text-right adv-num whitespace-nowrap">
        {formatNumber(line.margin, 'euro')}
      </td>
      <td className="px-4 py-2 align-middle">
        <div className="flex items-center justify-end gap-2">
          <div className="w-16 h-1.5 bg-adv-sunken rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${marginBarClass(line.marginPercentage)}`}
              style={{ width: `${Math.max(0, Math.min(100, line.marginPercentage))}%` }}
            />
          </div>
          <span
            className={`adv-num font-bold w-14 text-right ${marginTextClass(line.marginPercentage)}`}
          >
            {formatNumber(line.marginPercentage, 'percentage', 1)}
          </span>
        </div>
      </td>
    </tr>
  );
}

export function BusinessLinesTable({ segments }: BusinessLinesTableProps) {
  const [selectedCountries, setSelectedCountries] = useState<Set<string>>(new Set());
  const [selectedPriceTypes, setSelectedPriceTypes] = useState<Set<string>>(new Set());

  const countryOptions = useMemo(
    () => Array.from(new Set(segments.map((s) => s.country))).sort((a, b) => a.localeCompare(b)),
    [segments]
  );
  const priceTypeOptions = useMemo(
    () => Array.from(new Set(segments.map((s) => s.priceType))).sort((a, b) => a.localeCompare(b)),
    [segments]
  );

  // An empty selection means "everything", so the block opens showing the full
  // picture rather than an empty table nobody asked for.
  const lines = useMemo(() => {
    const filtered = segments.filter(
      (segment) =>
        (selectedCountries.size === 0 || selectedCountries.has(segment.country)) &&
        (selectedPriceTypes.size === 0 || selectedPriceTypes.has(segment.priceType))
    );
    return aggregateLines(filtered);
  }, [segments, selectedCountries, selectedPriceTypes]);

  const totals = useMemo(
    () =>
      lines.reduce(
        (accumulator, line) => ({
          revenue: accumulator.revenue + line.revenue,
          forecastedRevenue: accumulator.forecastedRevenue + line.forecastedRevenue,
          mediaCost: accumulator.mediaCost + line.mediaCost,
          margin: accumulator.margin + line.margin,
        }),
        { revenue: 0, forecastedRevenue: 0, mediaCost: 0, margin: 0 }
      ),
    [lines]
  );

  const toggleCountry = useCallback((value: string) => {
    setSelectedCountries((current) => {
      const next = new Set(current);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }, []);

  const togglePriceType = useCallback((value: string) => {
    setSelectedPriceTypes((current) => {
      const next = new Set(current);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedCountries(new Set());
    setSelectedPriceTypes(new Set());
  }, []);

  if (segments.length === 0) return null;

  const hasFilters = selectedCountries.size > 0 || selectedPriceTypes.size > 0;
  const totalMarginPercentage = totals.revenue > 0 ? (totals.margin / totals.revenue) * 100 : 0;

  return (
    <div className="adv-card adv-card--featured overflow-hidden">
      <div className="p-6 pb-4">
        <h2 className="adv-section-title">Revenue & Margin by Business Line</h2>
        <p className="adv-section-desc mt-1">
          Each product split by how it was sold. Margin is revenue minus media cost, over actual
          revenue only — the forecast carries no cost.
        </p>

        <div className="mt-4 p-4 bg-adv-sunken rounded-adv-lg space-y-3">
          <div className="flex items-center justify-between gap-4">
            <span className="text-adv-label font-bold text-adv-primary uppercase tracking-wide">
              Filters for this block
            </span>
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="adv-link text-adv-body-s font-bold"
              >
                Clear filters
              </button>
            )}
          </div>

          <FilterChips
            label="Market"
            options={countryOptions}
            selected={selectedCountries}
            onToggle={toggleCountry}
          />
          <FilterChips
            label="Price"
            options={priceTypeOptions}
            selected={selectedPriceTypes}
            onToggle={togglePriceType}
          />

          <p className="text-adv-body-s text-adv-secondary pt-2 border-t border-adv-border">
            These filters apply to this block only — the Daily Revenue chart above always shows
            every market and price type.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-6 mt-4 text-adv-body-s text-adv-secondary">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: darkStop(REVENUE_COLORS.served) }} />
            <span>Served revenue</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: darkStop(REVENUE_COLORS.closing) }} />
            <span>Closing revenue</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: darkStop(REVENUE_COLORS.forecast) }} />
            <span>Forecasted revenue</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 rounded-full" style={{ backgroundColor: COLORS.margin }} />
            <span>Margin %</span>
          </div>
        </div>
      </div>

      {lines.length === 0 ? (
        <div className="adv-state border-t border-adv-border">
          <p className="adv-state__desc">No business line matches these filters.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="adv-table">
            <thead className="bg-adv-sunken border-t border-adv-border">
              <tr>
                <th className="px-4 py-3 text-left">
                  Business Line
                </th>
                <th className="px-2 py-3 text-left">
                  Daily Revenue &amp; Margin
                </th>
                <th className="px-4 py-3 text-right">
                  Sold
                </th>
                <th className="px-4 py-3 text-right">
                  Bought
                </th>
                <th className="px-4 py-3 text-right">
                  Actual
                </th>
                <th className="px-4 py-3 text-right">
                  Forecast
                </th>
                <th className="px-4 py-3 text-right">
                  Media Cost
                </th>
                <th className="px-4 py-3 text-right">
                  Margin
                </th>
                <th className="px-4 py-3 text-right">
                  Margin %
                </th>
              </tr>
            </thead>

            <tbody>
              {lines.map((line) => (
                <BusinessLineRow key={line.key} line={line} />
              ))}
            </tbody>

            <tfoot>
              <tr>
                <td className="px-4 py-3 font-bold">Total</td>
                <td className="px-2 py-3 text-adv-body-s text-adv-secondary">
                  {lines.length} business lines{hasFilters ? ' (filtered)' : ''}
                </td>
                <td className="px-4 py-3" />
                <td className="px-4 py-3" />
                <td className="px-4 py-3 text-right adv-num font-bold whitespace-nowrap">
                  {formatNumber(totals.revenue, 'euro')}
                </td>
                <td className="px-4 py-3 text-right adv-num font-bold text-adv-secondary whitespace-nowrap">
                  {formatNumber(totals.forecastedRevenue, 'euro')}
                </td>
                <td className="px-4 py-3 text-right adv-num font-bold text-adv-secondary whitespace-nowrap">
                  {formatNumber(totals.mediaCost, 'euro')}
                </td>
                <td className="px-4 py-3 text-right adv-num font-bold whitespace-nowrap">
                  {formatNumber(totals.margin, 'euro')}
                </td>
                <td
                  className={`px-4 py-3 text-right adv-num font-bold whitespace-nowrap ${marginTextClass(totalMarginPercentage)}`}
                >
                  {formatNumber(totalMarginPercentage, 'percentage', 1)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
